/**
 * Google AdMob in the native apps.
 *
 * Rewarded ads use Server-Side Verification: the ad session nonce from our server is passed as SSV
 * `customData`, AdMob calls our `/api/v1/ads/ssv/admob` endpoint, and the server credits the coins.
 * The client never grants rewards itself — it only waits for the wallet update.
 */
import { isNative, platform } from './platform';

// Google's public test ad unit ids — replaced via VITE_ADMOB_* at build time for production.
const TEST_UNITS = {
  android: { rewarded: 'ca-app-pub-3940256099942544/5224354917', interstitial: 'ca-app-pub-3940256099942544/1033173712', banner: 'ca-app-pub-3940256099942544/6300978111' },
  ios: { rewarded: 'ca-app-pub-3940256099942544/1712485313', interstitial: 'ca-app-pub-3940256099942544/4411468910', banner: 'ca-app-pub-3940256099942544/2934735716' },
};

const env = import.meta.env as Record<string, string | undefined>;
const testing = env.VITE_ADMOB_TESTING !== 'false';

function unit(kind: 'rewarded' | 'interstitial' | 'banner'): string {
  const p = platform === 'ios' ? 'ios' : 'android';
  const key = `VITE_ADMOB_${p.toUpperCase()}_${kind.toUpperCase()}`;
  return env[key] ?? TEST_UNITS[p][kind];
}

let initialized: Promise<boolean> | null = null;

/** Initialises AdMob after the UMP consent flow (GDPR/CCPA) and iOS tracking prompt. */
export function initAds(): Promise<boolean> {
  if (!isNative) return Promise.resolve(false);
  initialized ??= (async () => {
    try {
      const { AdMob } = await import('@capacitor-community/admob');
      await AdMob.initialize({ initializeForTesting: testing });
      try {
        const info = await AdMob.requestConsentInfo();
        if (info.isConsentFormAvailable && info.status === 'REQUIRED') await AdMob.showConsentForm();
      } catch {
        /* consent service unavailable → non-personalised ads */
      }
      if (platform === 'ios') await AdMob.requestTrackingAuthorization().catch(() => undefined);
      return true;
    } catch {
      return false;
    }
  })();
  return initialized;
}

/** Shows a rewarded ad bound to a server ad session. Resolves true if the user earned the reward. */
export async function showRewardedAd(customData: string, userId: string): Promise<boolean> {
  if (!(await initAds())) return false;
  const { AdMob } = await import('@capacitor-community/admob');
  await AdMob.prepareRewardVideoAd({ adId: unit('rewarded'), isTesting: testing, ssv: { customData, userId } });
  const reward = await AdMob.showRewardVideoAd();
  return Boolean(reward && reward.amount >= 0);
}

let matchesSinceInterstitial = 0;

/** Interstitials only between matches (never during play), paced by the admin-configured frequency. */
export async function maybeShowInterstitial(everyN: number): Promise<void> {
  if (!isNative || everyN <= 0) return;
  matchesSinceInterstitial++;
  if (matchesSinceInterstitial < everyN) return;
  matchesSinceInterstitial = 0;
  if (!(await initAds())) return;
  try {
    const { AdMob } = await import('@capacitor-community/admob');
    await AdMob.prepareInterstitial({ adId: unit('interstitial'), isTesting: testing });
    await AdMob.showInterstitial();
  } catch {
    /* no fill / failure is fine */
  }
}

export async function showLobbyBanner(bottomOffsetPx: number): Promise<void> {
  if (!(await initAds())) return;
  try {
    const { AdMob, BannerAdPosition, BannerAdSize } = await import('@capacitor-community/admob');
    await AdMob.showBanner({ adId: unit('banner'), isTesting: testing, adSize: BannerAdSize.ADAPTIVE_BANNER, position: BannerAdPosition.BOTTOM_CENTER, margin: bottomOffsetPx });
  } catch {
    /* ignore */
  }
}

export async function hideBanner(): Promise<void> {
  if (!isNative) return;
  try {
    const { AdMob } = await import('@capacitor-community/admob');
    await AdMob.removeBanner();
  } catch {
    /* ignore */
  }
}
